import {
  allPass,
  always,
  any,
  anyPass,
  applySpec,
  complement,
  cond,
  curry,
  equals,
  filter,
  find,
  gt,
  hasPath,
  head,
  identity,
  ifElse,
  includes,
  isEmpty,
  isNil,
  keys,
  length,
  lt,
  map,
  multiply,
  not,
  nth,
  path,
  pathEq,
  pathOr,
  pickBy,
  pipe,
  pluck,
  prop,
  propEq,
  propOr,
  reject,
  replace,
  sum,
  T,
  uniq,
  values,
  __,
  flatten
} from 'ramda'
import { utils } from '@digital-commerce/shared-utils'

const { getTopLevelItems, _getItems, getHSService, isBusinessCart, checkProvisioner } = utils.cart

const getSecurity = getHSService(['security'])

const hasSecureBusiness = allPass([pipe(getSecurity, complement(isNil)), isBusinessCart])

export const FLOW_TYPES = {
  REGULAR: 'REGULAR',
  RESERVATION: 'RESERVATION'
}

export const hasLWC = (cart) => pipe(_getItems, any(pipe(prop('tags'), includes('lwc'))))(cart)

export const getMobilityOffers = pipe(
  prop('cart'),
  getTopLevelItems,
  filter(propEq('category', 'MOBILITY_OFFER'))
)

export const getMobilityOffersWithItems = (cart) =>
  pipe(
    getMobilityOffers,
    map((offer) => ({
      ...offer,
      items: pickBy((val, key) => includes(key, offer.items), cart.items)
    }))
  )

export const isABYODDevice = allPass([
  any(propEq('category', 'MOBILITY_SIM')),
  complement(any(propEq('category', 'MOBILITY_PHONE'))),
  complement(any(propEq('category', 'MOBILITY_TABLET')))
])

export const getBreakdownCategory = propOr('MH', 'category')

export const getBYODOffers = pipe(
  getMobilityOffersWithItems,
  filter(pipe(_getItems, isABYODDevice))
)

export const isEligibleToMount = pipe(getBYODOffers, complement(isEmpty))

export const isBYODCart = isEligibleToMount

const _checkCategory = (category) => pipe(head, propEq('category', category))

const _isMobilityOnly = allPass([pipe(length, equals(1)), _checkCategory('MOBILITY_TOTAL')])

const _isHSOnly = allPass([pipe(length, equals(1)), _checkCategory('HS_TOTAL')])

const minimumKeysInTotalPriceObject = 1

export const getPaymentBreakdownConfiguration = pipe(
  filter(
    allPass([
      pipe(propEq('category', 'TOTAL'), not),
      pipe(keys, length, gt(__, minimumKeysInTotalPriceObject))
    ])
  ),
  cond([
    [_isMobilityOnly, applySpec({ isMobility: T, totalPrice: head })],
    [_isHSOnly, applySpec({ isHS: T, totalPrice: head })],
    [T, applySpec({ isMH: T, totalPrice: identity })]
  ])
)

export const getTotalPriceByCategory = curry((category, totalPrices) =>
  pipe(filter(propEq('category', category)), head)(totalPrices)
)

const getTranslations = (key, lang) => {
  const translations = {
    en: { free: 'Free' },
    fr: { free: 'Gratuite' }
  }

  return path([lang, key])(translations)
}

export const displayShippingValue = ({ cost, lang, getCostValue }) => {
  if (cost === 0) return getTranslations('free', lang)
  return getCostValue(cost, lang)
}

export const hasAttribute = (string, attribute) => includes(string)(attribute)

export const formatCurrency = (num, lang, minimumFractionDigits = 2) => {
  const costFormatter = new Intl.NumberFormat(`${lang}-CA`, {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits
  })

  const result = !num ? costFormatter.format(0) : costFormatter.format(num)

  return result.replace('CA', '')
}

export const getFirstBillAmount = (total, credit) => {
  const firstBillAmount = total - credit
  return firstBillAmount < 0 ? 0 : firstBillAmount
}

export const parseCredit = (amount) => (amount > 0 ? -amount : 0)

export const getRemainingCredit = (credit, total, oneTime) => {
  const remainingCredit = credit - total - oneTime
  return remainingCredit < 0 ? 0 : remainingCredit
}

export const getInstallationFeeAmount = (cart) => {
  // dev notes: this will return 0 if no items with installation-fee tag are found
  const getTotalInstallFees = pipe(
    prop('items'),
    values,
    filter(
      pipe(
        prop('tags'),
        anyPass([
          allPass([includes('installation-fee'), complement(includes('business-connect'))]),
          includes('site-visit-fee'),
          includes('virtual-installation-fee')
        ])
      )
    ),
    map(path(['totalPrice', 'recurring', 0, 'recurringBreakdowns', 0, 'total'])),
    sum
  )(cart)

  /**
   * Gets discount total of all cart items by parsing
   * alterations filtered by 'installation-fee-discount' tag
   * @returns {number} for total of 'amount' properties in alterations or 0
   */
  const getTotalInstallDiscounts = pipe(
    prop('items'),
    values,
    map(pathOr([], ['itemPrice', 'recurring'])),
    flatten,
    map(propOr([], 'alterations')),
    flatten,
    filter(pipe(propOr([], ['tags']), includes('installation-fee-discount'))),
    map(propOr(0, ['amount'])),
    sum
  )(cart)

  const getTotalInstallFee = () => {
    const totalInstallFees = getTotalInstallFees
    const totalInstallDiscounts = getTotalInstallDiscounts
    // prevent negative value if discounts > fees
    if (totalInstallFees >= totalInstallDiscounts) {
      return totalInstallFees - totalInstallDiscounts
    }
    // return only fee if there are no discounts
    if (!totalInstallDiscounts) return totalInstallFees
    // if discounts > fee, return $0
    return 0
  }

  return getTotalInstallFee()
}

export const isAppointmentRequired = pipe(
  _getItems,
  any(
    allPass([
      pipe(propOr([], 'tags'), includes('work-offer')),
      pipe(propOr([], 'action'), anyPass([equals('ADD'), equals('MODIFY')]))
    ])
  )
)

/**
 * COMPASS cases are hardcoded to true.
 * This is because we do not have the isAppointmentRequired value from compass api in cart mfe
 * (in /resources in UCC).
 * In UCC, isAppointmentRequired is defaulted to true in SBS cases and when
 * this compass api fails.
 */
export const hasOneTimeInstallationFee = (cart) =>
  anyPass([
    allPass([checkProvisioner('COMPASS'), always(true)]),
    allPass([checkProvisioner('FIFA'), isAppointmentRequired]),
    hasSecureBusiness
  ])(cart)

export const getIrpcSummarydMonthlyBill = (recurringBreakdowns) => {
  const monthlyBill = pipe(
    reject(allPass([propEq('start', 1), propEq('duration', 1)])),
    reject(pipe(propOr(1, 'duration'), allPass([lt(__, 6), gt(__, 0)]))),
    filter(pipe(propOr(1, 'start'), lt(__, 25))),
    nth(0)
  )(recurringBreakdowns)

  return propOr(0, 'total')(monthlyBill)
}

export const getInTermMonthlyBillAmount = (recurringBills, isHS) => {
  const inTermRecurringBills = isHS
    ? recurringBills.filter((b) => b.start < 13)
    : recurringBills.filter((b) => b.start < 25)

  if (inTermRecurringBills.length > 1) {
    const bill = inTermRecurringBills[inTermRecurringBills.length - 1]
    return isHS ? bill.total : bill.subTotal
  }

  const isFirstBill = inTermRecurringBills.length === 1 && inTermRecurringBills[0].duration === 1
  if (isFirstBill || inTermRecurringBills.length === 0) {
    return 0
  }

  const [bill] = inTermRecurringBills
  return isHS ? bill.total : bill.subTotal
}

export const getMonthlyBillAmount = (recurring, isIrpcFlow) => {
  if (recurring.length === 0) return 0

  if (isIrpcFlow) {
    return getIrpcSummarydMonthlyBill(recurring[0].recurringBreakdowns)
  }
  return getInTermMonthlyBillAmount(recurring[0].recurringBreakdowns, true)
}

export const getCustomerType = (customer) => {
  const isMobilityType = hasPath(['mobility', 'type'])(customer)
  const isHsType = hasPath(['hs', 'type'])(customer)

  if (!customer || !(isMobilityType || isHsType)) return ''

  const customerTypeMap = {
    'C|E': 'epp',
    'B|P': 'business',
    'B|R': 'business',
    'I|F': 'twa-legacy'
  }

  const mobCustomerTypeMap = {
    'I|R': 'consumer',
    ...customerTypeMap
  }

  const hsCustomerTypeMap = {
    'R|I': 'consumer',
    ...customerTypeMap
  }

  return isHsType
    ? pipe(prop('hs'), ({ type, subType }) => hsCustomerTypeMap[`${type}|${subType}`])(customer)
    : pipe(
        prop('mobility'),
        ({ type, subType }) => mobCustomerTypeMap[`${type}|${subType}`]
      )(customer)
}

export const getRecurringHeading = (
  category,
  translations,
  // eslint-disable-next-line no-shadow
  isBusinessCart = false
) => {
  if (isBusinessCart) return translations.monthlyBillsSBS
  return cond([
    [equals('HS_TOTAL'), always(translations.monthlyBillsHS)],
    [equals('MOBILITY_TOTAL'), always(translations.monthlyBillsMobility)],
    [T, always(translations.monthlyBillsMH)]
  ])(category)
}

export const getCancellationFee = (service) => ({
  cancellationFeeAmount: path(['contractTerm', 'cancellationFee', 'amount'], service),
  cancellationFeeAmortizedAmount: path(
    ['contractTerm', 'cancellationFee', 'amortizedAmount'],
    service
  )
})

export const transformCancellationFeeContent = curry((service, lang, content) => {
  const { cancellationFeeAmount, cancellationFeeAmortizedAmount } = getCancellationFee(service)

  return pipe(
    replace('#cancellationFeeAmount#', formatCurrency(cancellationFeeAmount, lang)),
    replace(
      '#cancellationFeeAmortizedAmount#',
      formatCurrency(cancellationFeeAmortizedAmount, lang)
    ),
    replace(
      '#partialCancellationFeeAmortizedAmount#',
      formatCurrency(multiply(cancellationFeeAmortizedAmount, 0.5), lang)
    )
  )(content)
})

export const getFlowType = (cart) =>
  pipe(
    _getItems,
    find(
      allPass([
        pathEq(['fulfillment', 'inStorePickupEligible'], true),
        pathEq(['fulfillment', 'method'], 'IN_STORE')
      ])
    ),
    ifElse(isNil, always(FLOW_TYPES.REGULAR), always(FLOW_TYPES.RESERVATION))
  )(cart)

const getTotalPrice = pipe(path(['root', 'totalPrices']), find(propEq('category', 'TOTAL')))

export const getPriceOneTimeCredit = pipe(getTotalPrice, pathOr(0, ['oneTime', 'credit']))

export const getPriceOneTimeTotal = pipe(getTotalPrice, path(['oneTime', 'total']))

export const checkIfCartHasHSItemsOnly = (cart) => {
  const itemCategoryList = pipe(prop('items'), values, pluck('category'), uniq)(cart)

  return itemCategoryList.every((category) => category.lastIndexOf('HS_', 0) === 0)
}

export const getPriceOneTimeSubtotal = pipe(getTotalPrice, path(['oneTime', 'subTotal']))

export const getPriceOneTimeTaxes = pipe(
  getTotalPrice,
  path(['oneTime', 'taxes']),
  filter((tax) => prop('rate')(tax) !== 0)
)

export const getShippingValue = pipe(
  getTotalPrice,
  ifElse(hasPath(['oneTime', 'shipping']), path(['oneTime', 'shipping']), always(0))
)
